import React from 'react';
import { ColumnsType, ColumnType } from 'antd/es/table';
import { ShopifyProduct } from '../../../store/shopifyProductsSlice';
import { Tag, Tooltip, Image, Skeleton } from 'antd';
import { renderLimitedTags } from '../../../utils/renderLimitedTags';
import { GenerationStatus, SyncStatus } from '../../../constants/status';
import { statusColorGeneration, statusColorSync } from '../../../constants/statusColors';

interface ColumnsProps {
    handleShopifyProductDetailModalOpen: (shopifyProduct: ShopifyProduct) => void;
    handleImageCountClicked: (shopifyProduct: ShopifyProduct) => void;
    tagFilterProps: ColumnType<ShopifyProduct>;
    productTypeFilterProps: ColumnType<ShopifyProduct>;
    vendorFilterProps: ColumnType<ShopifyProduct>;
    hasDescriptionFilters: { text: string; value: boolean }[];
    syncStatusFilters: { text: string; value: SyncStatus }[];
    generationStatusFilters: { text: string; value: GenerationStatus }[];
}

export const getColumns = ({
    handleShopifyProductDetailModalOpen,
    handleImageCountClicked,
    tagFilterProps,
    productTypeFilterProps,
    vendorFilterProps,
    hasDescriptionFilters,
    syncStatusFilters,
    generationStatusFilters
}: ColumnsProps): ColumnsType<ShopifyProduct> => [
        {
            title: 'Image',
            dataIndex: 'imageUrl',
            key: 'imageUrl',
            width: 70,
            render: (image) => (
                <div style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                    {
                        image ? (
                            <Image
                                width={40}
                                height={40}
                                src={image}
                                fallback="https://via.placeholder.com/40" // Placeholder image if loading fails
                                style={{ objectFit: 'cover' }}
                            />
                        ) : (
                            <Skeleton.Avatar active size="small" shape="square" />
                        )}
                </div>
            ),
            fixed: 'left'
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            width: 250,
            fixed: 'left',
            render: (title, shopifyProduct) => (
                <Tooltip title={title} >
                    <a
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent row-level event conflicts
                            handleShopifyProductDetailModalOpen(shopifyProduct);
                        }}
                    >
                        {title}
                    </a>
                </Tooltip>
            ),
        },
        {
            title: 'Has Description',
            dataIndex: 'descriptionHtml',
            key: 'descriptionHtml',
            render: (descriptionHtml, record) => (
                <Tag key={record.id + '_has_desciption'} > {descriptionHtml ? "Yes" : "No"} </Tag>
            ),
            filters: hasDescriptionFilters,
            // onFilter: (value, record) => !!record.bodyHtml === value,
            width: 120,
        },
        {
            title: 'Vendor',
            dataIndex: 'vendor',
            key: 'vendor',
            ...vendorFilterProps
        },
        {
            title: 'Product Type',
            dataIndex: 'productType',
            key: 'productType',
            ...productTypeFilterProps
        },
        {
            title: 'Tags',
            dataIndex: 'tags',
            key: 'tags',
            render: (tags: string[]) => renderLimitedTags(tags, 2),
            ...tagFilterProps
        },
        {
            title: 'Media Count',
            dataIndex: ['mediaCount', 'count'],
            key: 'mediaCount',
            width: 100,
            sorter: (a, b) => (a.mediaCount?.count || 0) - (b.mediaCount?.count || 0),
            render: (mediaCount, shopifyProduct) => <a onClick={() => handleImageCountClicked(shopifyProduct)}> {mediaCount} </a>
        },
        {
            title: 'Variants',
            dataIndex: ['variantsCount', 'count'],
            key: 'variantsCount',
            width: 100,
            sorter: (a, b) => (a.variantsCount?.count || 0) - (b.variantsCount?.count || 0), // Numeric sorting
        },
        {
            title: 'Shopify Status',
            dataIndex: 'shopifyStatus',
            key: 'shopifyStatus',
            width: 150,
            sorter: (a, b) => a.shopifyStatus.localeCompare(b.shopifyStatus), // Alphabetical sorting
            // TODO: add filter
        },
        {
            title: 'Sync Status',
            dataIndex: 'syncStatus',
            key: 'syncStatus',
            width: 150,
            filters: syncStatusFilters,
            filterMultiple: false,
            // onFilter: (value, record) => record.status === value,
            sorter: (a, b) => a.syncStatus.localeCompare(b.syncStatus), // Alphabetical sorting
            render: (status: SyncStatus) => (
                <Tag color={statusColorSync[status]} >
                    {status}
                </Tag>
            )
        },
        {
            title: 'Generation Status',
            dataIndex: 'generationStatus',
            key: 'generationStatus',
            width: 150,
            filters: generationStatusFilters,
            filterMultiple: false,
            // onFilter: (value, record) => record.status === value,
            sorter: (a, b) => (a.generationStatus || "").localeCompare(b.generationStatus || ""), // Alphabetical sorting
            render: (status: GenerationStatus) => (
                <Tag color={statusColorGeneration[status]} >
                    {status}
                </Tag>
            )
        }
    ];
